import { useCallback, useEffect, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import {
  RotateCcw,
  RotateCw,
  Check,
  X,
  Sparkles,
  Loader2,
} from "lucide-react";
import { cn } from "../lib/utils";

const FILTERS: { id: string; label: string; css: string }[] = [
  { id: "none", label: "Original", css: "none" },
  { id: "vivid", label: "Vivid", css: "saturate(1.4) contrast(1.1)" },
  { id: "warm", label: "Warm", css: "sepia(0.25) saturate(1.3) hue-rotate(-10deg)" },
  { id: "cool", label: "Cool", css: "saturate(1.1) hue-rotate(15deg) brightness(1.05)" },
  { id: "bw", label: "SW", css: "grayscale(1) contrast(1.1)" },
  { id: "fade", label: "Fade", css: "saturate(0.7) contrast(0.95) brightness(1.08)" },
  { id: "noir", label: "Noir", css: "grayscale(1) contrast(1.4) brightness(0.9)" },
];

interface PhotoEditorProps {
  /** Original-File vom input */
  file: File;
  /** Cropped+filtered Blob — bereit zum Upload */
  onConfirm: (blob: Blob) => void;
  onCancel: () => void;
}

/**
 * Modales Foto-Editor: Crop (3:4 aspect), Rotation, Filter-Preset.
 * Benutzt Canvas für finales Rendering — Filter werden als CSS ins Canvas gebakt.
 */
export function PhotoEditor({ file, onConfirm, onCancel }: PhotoEditorProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>(FILTERS[0]);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback(
    (_: Area, areaPixels: Area) => setCroppedArea(areaPixels),
    [],
  );

  async function confirm() {
    if (!imageSrc || !croppedArea) return;
    setWorking(true);
    try {
      const blob = await renderCropped(imageSrc, croppedArea, rotation, filter.css);
      onConfirm(blob);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-ink-900 text-white">
      <header className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <button
          type="button"
          onClick={onCancel}
          aria-label="Abbrechen"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20"
        >
          <X className="h-5 w-5" />
        </button>
        <span className="text-sm font-semibold tracking-wide">Foto bearbeiten</span>
        <button
          type="button"
          onClick={confirm}
          disabled={working || !croppedArea}
          aria-label="Bestätigen"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-gradient shadow-glow transition disabled:opacity-50"
        >
          {working ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Check className="h-5 w-5" />
          )}
        </button>
      </header>

      <div className="relative flex-1 bg-black" style={{ filter: filter.css }}>
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={3 / 4}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            showGrid
            objectFit="contain"
            style={{
              containerStyle: { background: "#000" },
              mediaStyle: {},
            }}
          />
        )}
      </div>

      <div className="border-t border-white/10 bg-ink-900 pb-[max(env(safe-area-inset-bottom),1rem)]">
        <div className="px-4 pt-4">
          <div className="mb-3 flex items-center justify-between text-xs uppercase tracking-widest text-white/60">
            <span>Zoom</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
                aria-label="Nach links drehen"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                aria-label="Nach rechts drehen"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20"
              >
                <RotateCw className="h-4 w-4" />
              </button>
            </div>
          </div>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-brand-500"
            aria-label="Zoom"
          />
        </div>

        <div className="mt-4 px-4">
          <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-widest text-white/60">
            <Sparkles className="h-3.5 w-3.5" />
            Filter
          </div>
          <div className="flex gap-2 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f)}
                className={cn(
                  "flex shrink-0 flex-col items-center gap-1.5 rounded-xl p-2 transition",
                  filter.id === f.id
                    ? "bg-white/15 ring-2 ring-brand-500"
                    : "hover:bg-white/5",
                )}
              >
                <div
                  className="h-12 w-12 rounded-lg bg-brand-gradient"
                  style={{ filter: f.css }}
                />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-white/80">
                  {f.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

async function renderCropped(
  imageSrc: string,
  area: Area,
  rotation: number,
  cssFilter: string,
): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const rad = (rotation * Math.PI) / 180;

  const safe = Math.max(image.width, image.height) * 2;
  const temp = document.createElement("canvas");
  temp.width = safe;
  temp.height = safe;
  const tctx = temp.getContext("2d");
  if (!tctx) throw new Error("Canvas-Context fehlt");
  tctx.filter = cssFilter;
  tctx.translate(safe / 2, safe / 2);
  tctx.rotate(rad);
  tctx.drawImage(image, -image.width / 2, -image.height / 2);

  const out = document.createElement("canvas");
  out.width = area.width;
  out.height = area.height;
  const octx = out.getContext("2d");
  if (!octx) throw new Error("Output-Context fehlt");
  octx.drawImage(
    temp,
    safe / 2 - image.width / 2 + area.x,
    safe / 2 - image.height / 2 + area.y,
    area.width,
    area.height,
    0,
    0,
    area.width,
    area.height,
  );

  return await new Promise<Blob>((resolve, reject) =>
    out.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Export fehlgeschlagen"))),
      "image/jpeg",
      0.92,
    ),
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
